// HeroTemplate — design spec §1.
// One massive headline center stage. The 80/20 default.
// Reference: Apple "Apple Vision Pro" reveal slide (June 2023, ~01:23:00).
//
// Acceptance:
//  - Headline occupies <50% vertical viewport but is unmistakably the focal point.
//  - Top and bottom each ≥20% empty.
//  - max 3 lines on desktop, 4 on mobile.
//  - text-balance, tight tracking.
//  - Orb morphs INTO position via layoutId (does not pop).

import { motion } from 'framer-motion';
import { Stage } from '../stage/Stage';
import { Orb } from '../stage/Orb';
import { Eyebrow } from '../stage/Eyebrow';
import { fadeUp, stagger } from '../motion/variants';
import type { SceneContent } from '../types';

interface HeroProps {
  content: SceneContent;
}

export function HeroTemplate({ content }: HeroProps) {
  const { headline = '', body, eyebrow } = content;
  return (
    <Stage>
      <motion.div
        initial="hidden"
        animate="show"
        exit="exit"
        variants={stagger(0.12)}
        className="m-auto max-w-[1280px] w-full grid grid-rows-[1fr_auto_1fr] place-items-center text-center"
      >
        {/* Row 1: orb anchor + eyebrow, bottom-aligned in its row */}
        <motion.div className="flex flex-col items-center gap-8 self-end pb-12">
          <Orb size={88} />
          {eyebrow && (
            <motion.div variants={fadeUp}>
              <Eyebrow>{eyebrow}</Eyebrow>
            </motion.div>
          )}
        </motion.div>

        {/* Row 2: headline — the focal point */}
        <motion.h1
          variants={fadeUp}
          className="text-display-1 font-display max-w-[18ch] text-balance text-fg"
        >
          {headline}
        </motion.h1>

        {/* Row 3: subtitle, top-aligned */}
        <div className="self-start mt-8">
          {body && (
            <motion.p
              variants={fadeUp}
              className="text-subhead text-fg-soft max-w-[52ch] text-pretty"
            >
              {body}
            </motion.p>
          )}
        </div>
      </motion.div>
    </Stage>
  );
}
